<template>
  <div class="create-resolution-page">
    <!-- Page Header -->
    <div class="d-flex align-center mb-6">
      <v-btn
        icon
        variant="text"
        @click="$router.go(-1)"
        class="mr-4"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div>
        <h1 class="text-h4 font-weight-bold text-primary">
          {{ isEditing ? 'Edit Resolution' : 'Create New Resolution' }}
        </h1>
        <p class="text-subtitle-1 text-medium-emphasis">
          {{ isEditing ? 'Modify resolution details and voting settings' : 'Draft a new board resolution for review and voting' }}
        </p>
      </div>
    </div>

    <v-form ref="resolutionForm" v-model="formValid" @submit.prevent="saveResolution">
      <v-row>
        <!-- Main Form -->
        <v-col cols="12" md="8">
          <!-- Basic Information -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Resolution Details
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="resolution.title"
                    label="Resolution Title *"
                    :rules="titleRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-text"
                    placeholder="e.g., Approval of Annual Budget for 2025"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="resolution.resolution_number"
                    label="Resolution Number *"
                    :rules="resolutionNumberRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-identifier"
                    placeholder="e.g., RES-2025-001"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="resolution.type"
                    :items="resolutionTypes"
                    label="Resolution Type *"
                    :rules="typeRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-shape"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="resolution.priority"
                    :items="priorityOptions"
                    label="Priority Level *"
                    :rules="priorityRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-flag"
                    required
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="resolution.description"
                    label="Resolution Description *"
                    :rules="descriptionRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-text-long"
                    placeholder="Provide a detailed description of the resolution, including background, purpose, and expected outcomes"
                    rows="4"
                    auto-grow
                    required
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="resolution.proposed_action"
                    label="Proposed Action *"
                    :rules="actionRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-check-bold"
                    placeholder="Clearly state the specific action(s) to be taken if this resolution is approved"
                    rows="3"
                    auto-grow
                    required
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="resolution.rationale"
                    label="Rationale & Justification"
                    variant="outlined"
                    prepend-inner-icon="mdi-lightbulb"
                    placeholder="Explain the reasoning and justification for this resolution"
                    rows="3"
                    auto-grow
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Financial Impact (Optional) -->
          <v-card class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">Financial Impact</span>
              <v-switch
                v-model="hasFinancialImpact"
                label="Has Financial Impact"
                color="primary"
                hide-details
              />
            </v-card-title>
            <v-card-text v-if="hasFinancialImpact">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="resolution.financial_impact.estimated_cost"
                    label="Estimated Cost"
                    type="number"
                    variant="outlined"
                    prepend-inner-icon="mdi-currency-usd"
                    placeholder="0.00"
                    step="0.01"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="resolution.financial_impact.budget_source"
                    :items="budgetSources"
                    label="Budget Source"
                    variant="outlined"
                    prepend-inner-icon="mdi-bank"
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="resolution.financial_impact.justification"
                    label="Financial Justification"
                    variant="outlined"
                    rows="2"
                    auto-grow
                    placeholder="Explain the financial necessity and expected return on investment"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Supporting Documents -->
          <v-card class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">Supporting Documents</span>
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                @click="showDocumentDialog = true"
                prepend-icon="mdi-plus"
              >
                Add Document
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div v-if="resolution.documents.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey" class="mb-2">
                  mdi-file-document-multiple
                </v-icon>
                <p class="text-medium-emphasis">
                  No documents attached. Add supporting documents to strengthen your resolution.
                </p>
              </div>

              <div v-else>
                <div
                  v-for="(document, index) in resolution.documents"
                  :key="index"
                  class="document-item d-flex align-center pa-3 mb-2"
                >
                  <v-icon
                    :color="getDocumentColor(document.type)"
                    class="mr-3"
                  >
                    {{ getDocumentIcon(document.type) }}
                  </v-icon>
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">{{ document.title }}</div>
                    <div class="text-caption text-medium-emphasis">{{ document.description }}</div>
                  </div>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    color="error"
                    @click="removeDocument(index)"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" md="4">
          <!-- Meeting Association -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Meeting Association
            </v-card-title>
            <v-card-text>
              <v-select
                v-model="resolution.meeting_id"
                :items="meetings"
                item-title="title"
                item-value="id"
                label="Related Meeting"
                variant="outlined"
                prepend-inner-icon="mdi-calendar"
                clearable
              />
              <v-text-field
                v-model="resolution.agenda_item"
                label="Agenda Item Number"
                variant="outlined"
                prepend-inner-icon="mdi-format-list-numbered"
                placeholder="e.g., 5.2"
                class="mt-4"
              />
            </v-card-text>
          </v-card>

          <!-- Voting Configuration -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Voting Settings
            </v-card-title>
            <v-card-text>
              <v-switch
                v-model="resolution.voting_enabled"
                label="Enable Voting"
                color="primary"
                hide-details
                class="mb-4"
              />

              <div v-if="resolution.voting_enabled">
                <v-select
                  v-model="resolution.voting_type"
                  :items="votingTypes"
                  label="Voting Type"
                  variant="outlined"
                  class="mb-4"
                />

                <v-text-field
                  v-model="resolution.quorum_required"
                  label="Quorum Required (%)"
                  type="number"
                  variant="outlined"
                  min="1"
                  max="100"
                  class="mb-4"
                />

                <v-text-field
                  v-model="resolution.majority_required"
                  label="Majority Required (%)"
                  type="number"
                  variant="outlined"
                  min="50"
                  max="100"
                  class="mb-4"
                />

                <v-text-field
                  v-model="resolution.voting_deadline"
                  label="Voting Deadline"
                  type="datetime-local"
                  variant="outlined"
                />
              </div>
            </v-card-text>
          </v-card>

          <!-- Additional Settings -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Additional Settings
            </v-card-title>
            <v-card-text>
              <v-switch
                v-model="resolution.is_confidential"
                label="Confidential Resolution"
                color="primary"
                hide-details
                class="mb-4"
              />

              <v-switch
                v-model="resolution.requires_implementation"
                label="Requires Implementation Tracking"
                color="primary"
                hide-details
                class="mb-4"
              />

              <v-combobox
                v-model="resolution.tags"
                label="Tags"
                variant="outlined"
                multiple
                chips
                closable-chips
                hint="Add tags to categorize this resolution"
                persistent-hint
              />
            </v-card-text>
          </v-card>

          <!-- Action Buttons -->
          <v-card>
            <v-card-text>
              <div class="d-flex flex-column gap-2">
                <v-btn
                  type="submit"
                  color="primary"
                  size="large"
                  block
                  :loading="saving"
                  :disabled="!formValid"
                >
                  {{ isEditing ? 'Update Resolution' : 'Create Resolution' }}
                </v-btn>

                <v-btn
                  variant="outlined"
                  size="large"
                  block
                  @click="saveDraft"
                  :disabled="saving"
                >
                  Save as Draft
                </v-btn>

                <v-btn
                  variant="text"
                  size="large"
                  block
                  @click="cancelCreation"
                >
                  Cancel
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <!-- Add Document Dialog -->
    <v-dialog v-model="showDocumentDialog" max-width="600">
      <v-card>
        <v-card-title>Add Supporting Document</v-card-title>
        <v-card-text>
          <v-form ref="documentForm" v-model="documentFormValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="newDocument.title"
                  label="Document Title *"
                  :rules="documentTitleRules"
                  variant="outlined"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="newDocument.type"
                  :items="documentTypes"
                  label="Document Type *"
                  :rules="documentTypeRules"
                  variant="outlined"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="newDocument.description"
                  label="Description"
                  variant="outlined"
                  rows="2"
                  auto-grow
                />
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="newDocument.file"
                  label="Upload File"
                  variant="outlined"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDocumentDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!documentFormValid"
            @click="addDocument"
          >
            Add Document
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export default {
  name: 'CreateResolution',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()

    // Form state
    const resolutionForm = ref(null)
    const documentForm = ref(null)
    const formValid = ref(false)
    const documentFormValid = ref(false)
    const saving = ref(false)
    const hasFinancialImpact = ref(false)
    const showDocumentDialog = ref(false)

    // Check if editing
    const isEditing = computed(() => !!route.params.id)

    // Resolution data
    const resolution = reactive({
      title: '',
      description: '',
      resolution_number: '',
      proposed_action: '',
      rationale: '',
      type: 'Standard',
      priority: 'Medium',
      meeting_id: null,
      agenda_item: '',
      voting_enabled: true,
      voting_type: 'Simple Majority',
      quorum_required: 50,
      majority_required: 50,
      voting_deadline: '',
      is_confidential: false,
      requires_implementation: false,
      tags: [],
      documents: [],
      financial_impact: {
        estimated_cost: '',
        budget_source: '',
        justification: ''
      }
    })

    // New document for dialog
    const newDocument = reactive({
      title: '',
      type: '',
      description: '',
      file: null
    })

    // Reference data
    const meetings = ref([])
    
    const resolutionTypes = [
      'Standard', 'Emergency', 'Administrative', 'Financial', 'Strategic', 'Policy'
    ]

    const priorityOptions = [
      { title: 'Low', value: 'Low' },
      { title: 'Medium', value: 'Medium' },
      { title: 'High', value: 'High' },
      { title: 'Urgent', value: 'Urgent' }
    ]

    const votingTypes = [
      'Simple Majority', 'Two-Thirds Majority', 'Unanimous', 'Plurality'
    ]

    const budgetSources = [
      'Operational Budget', 'Capital Budget', 'Reserve Fund', 'Special Allocation', 'External Funding'
    ]

    const documentTypes = [
      'Policy Document', 'Financial Report', 'Legal Document', 'Research Report', 
      'Presentation', 'Correspondence', 'Supporting Evidence', 'Other'
    ]

    // Validation rules
    const titleRules = [
      v => !!v || 'Title is required',
      v => v.length >= 10 || 'Title must be at least 10 characters',
      v => v.length <= 200 || 'Title must be less than 200 characters'
    ]

    const resolutionNumberRules = [
      v => !!v || 'Resolution number is required',
      v => /^[A-Z0-9-]+$/.test(v) || 'Resolution number must contain only uppercase letters, numbers, and hyphens',
      v => v.length <= 50 || 'Resolution number must be less than 50 characters'
    ]

    const typeRules = [
      v => !!v || 'Resolution type is required'
    ]

    const priorityRules = [
      v => !!v || 'Priority level is required'
    ]

    const descriptionRules = [
      v => !!v || 'Description is required',
      v => v.length >= 50 || 'Description must be at least 50 characters'
    ]

    const actionRules = [
      v => !!v || 'Proposed action is required',
      v => v.length >= 20 || 'Proposed action must be at least 20 characters'
    ]

    const documentTitleRules = [
      v => !!v || 'Document title is required'
    ]

    const documentTypeRules = [
      v => !!v || 'Document type is required'
    ]

    // Methods
    const getDocumentIcon = (type) => {
      const icons = {
        'Policy Document': 'mdi-file-document',
        'Financial Report': 'mdi-file-chart',
        'Legal Document': 'mdi-gavel',
        'Research Report': 'mdi-file-search',
        'Presentation': 'mdi-presentation',
        'Correspondence': 'mdi-email',
        'Supporting Evidence': 'mdi-file-check',
        'Other': 'mdi-file'
      }
      return icons[type] || 'mdi-file'
    }

    const getDocumentColor = (type) => {
      const colors = {
        'Policy Document': 'primary',
        'Financial Report': 'success',
        'Legal Document': 'warning',
        'Research Report': 'info',
        'Presentation': 'purple',
        'Correspondence': 'teal',
        'Supporting Evidence': 'indigo',
        'Other': 'grey'
      }
      return colors[type] || 'grey'
    }

    const addDocument = () => {
      if (!documentFormValid.value) return

      resolution.documents.push({
        title: newDocument.title,
        type: newDocument.type,
        description: newDocument.description,
        file: newDocument.file
      })

      // Reset form
      newDocument.title = ''
      newDocument.type = ''
      newDocument.description = ''
      newDocument.file = null
      
      showDocumentDialog.value = false
      toast.success('Document added successfully')
    }

    const removeDocument = (index) => {
      resolution.documents.splice(index, 1)
      toast.success('Document removed')
    }

    const saveResolution = async () => {
      if (!formValid.value) return

      saving.value = true
      try {
        // Map frontend fields to API fields
        const resolutionData = {
          title: resolution.title,
          description: resolution.description,
          resolution_number: resolution.resolution_number || `RES-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`, // Generate if not provided
          meeting_id: resolution.meeting_id,
          vote_type: resolution.voting_type === 'Simple Majority' ? 'simple' : 
                    resolution.voting_type === 'Unanimous' ? 'unanimous' : 'majority',
          type: resolution.type,
          priority: resolution.priority,
          status: 'voting', // Set status to voting when submitted (not draft)
          voting_deadline: resolution.voting_deadline || null,
          implementation_notes: resolution.proposed_action || null
        }

        let response
        if (isEditing.value) {
          response = await api.put(`/resolutions/${route.params.id}`, resolutionData)
          toast.success('Resolution updated successfully')
        } else {
          response = await api.post('/resolutions', resolutionData) // Remove trailing slash
          toast.success('Resolution created successfully')
        }

        router.push(`/resolutions/${response.data.resolution.id}`) // API returns wrapped in .resolution for create
      } catch (error) {
        console.error('Error saving resolution:', error)
        if (error.response?.data?.message) {
          toast.error(error.response.data.message)
        } else {
          toast.error('Failed to save resolution')
        }
      } finally {
        saving.value = false
      }
    }

    const saveDraft = async () => {
      saving.value = true
      try {
        const resolutionData = {
          title: resolution.title,
          description: resolution.description,
          resolution_number: resolution.resolution_number || `RES-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          meeting_id: resolution.meeting_id,
          vote_type: resolution.voting_type === 'Simple Majority' ? 'simple' : 
                    resolution.voting_type === 'Unanimous' ? 'unanimous' : 'majority',
          type: resolution.type,
          priority: resolution.priority,
          status: 'draft',
          voting_deadline: resolution.voting_deadline || null,
          implementation_notes: resolution.proposed_action || null
        }

        let response
        if (isEditing.value) {
          response = await api.put(`/resolutions/${route.params.id}`, resolutionData)
        } else {
          response = await api.post('/resolutions/', resolutionData)
        }

        toast.success('Resolution saved as draft')
        router.push('/resolutions')
      } catch (error) {
        console.error('Error saving draft:', error)
        toast.error('Failed to save draft')
      } finally {
        saving.value = false
      }
    }

    const cancelCreation = () => {
      router.go(-1)
    }

    // Data loading
    const loadMeetings = async () => {
      try {
        const response = await api.get('/meetings/')
        meetings.value = response.data.meetings || []
      } catch (error) {
        console.error('Error loading meetings:', error)
      }
    }

    const loadResolution = async () => {
      if (!isEditing.value) return

      try {
        const response = await api.get(`/resolutions/${route.params.id}`)
        const data = response.data
        
        // Map API data to form fields
        Object.assign(resolution, {
          title: data.title,
          description: data.description,
          resolution_number: data.resolution_number,
          type: data.type,
          priority: data.priority,
          proposed_action: data.implementation_notes,
          meeting_id: data.meeting_id,
          voting_deadline: data.voting_deadline,
          voting_type: data.vote_type === 'simple' ? 'Simple Majority' : 
                      data.vote_type === 'unanimous' ? 'Unanimous' : 'Two-Thirds Majority'
        })
        hasFinancialImpact.value = !!data.financial_impact
      } catch (error) {
        console.error('Error loading resolution:', error)
        toast.error('Failed to load resolution')
        router.push('/resolutions')
      }
    }

    // Initialize default values
    onMounted(() => {
      loadMeetings()
      loadResolution()
      
      // Set default voting deadline to 1 week from now
      const nextWeek = new Date()
      nextWeek.setDate(nextWeek.getDate() + 7)
      resolution.voting_deadline = nextWeek.toISOString().slice(0, 16)

      // Check for duplication
      if (route.query.duplicate) {
        // Load resolution to duplicate
        api.get(`/resolutions/${route.query.duplicate}`)
          .then(response => {
            const data = response.data.resolution
            Object.assign(resolution, {
              ...data,
              title: `Copy of ${data.title}`,
              id: null,
              status: 'draft'
            })
            hasFinancialImpact.value = !!data.financial_impact
          })
          .catch(error => {
            console.error('Error loading resolution to duplicate:', error)
          })
      }
    })

    return {
      // Form refs
      resolutionForm,
      documentForm,
      formValid,
      documentFormValid,
      saving,
      hasFinancialImpact,
      showDocumentDialog,

      // Computed
      isEditing,

      // Data
      resolution,
      newDocument,
      meetings,
      resolutionTypes,
      priorityOptions,
      votingTypes,
      budgetSources,
      documentTypes,

      // Validation rules
      titleRules,
      resolutionNumberRules,
      typeRules,
      priorityRules,
      descriptionRules,
      actionRules,
      documentTitleRules,
      documentTypeRules,

      // Methods
      getDocumentIcon,
      getDocumentColor,
      addDocument,
      removeDocument,
      saveResolution,
      saveDraft,
      cancelCreation
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.create-resolution-page {
  .text-medium-emphasis {
    opacity: 0.7;
  }

  .document-item {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }
  }
}
</style>